/**
 * Utility for managing seen clues in localStorage
 */

const SEEN_CLUES_KEY = 'cashstash_seen_clues';

/**
 * Get the IDs of clues that the user has seen
 * @param userId The user ID
 * @returns Array of seen clue IDs
 */
export const getSeenClues = (userId: string): string[] => {
  try {
    const storageKey = `${SEEN_CLUES_KEY}_${userId}`;
    const storedData = localStorage.getItem(storageKey);
    
    if (!storedData) {
      return [];
    }
    
    return JSON.parse(storedData);
  } catch (error) {
    console.error('Error getting seen clues from localStorage:', error);
    return [];
  }
};

/**
 * Mark a clue as seen
 * @param userId The user ID
 * @param clueId The clue ID to mark as seen
 */
export const markClueSeen = (userId: string, clueId: string): void => {
  try {
    const storageKey = `${SEEN_CLUES_KEY}_${userId}`;
    const seenClues = getSeenClues(userId);
    
    if (!seenClues.includes(clueId)) {
      seenClues.push(clueId);
      localStorage.setItem(storageKey, JSON.stringify(seenClues));
    }
  } catch (error) {
    console.error('Error marking clue as seen in localStorage:', error);
  }
};

/**
 * Mark multiple clues as seen
 * @param userId The user ID
 * @param clueIds Array of clue IDs to mark as seen
 */
export const markCluesSeen = (userId: string, clueIds: string[]): void => {
  try {
    const storageKey = `${SEEN_CLUES_KEY}_${userId}`;
    const seenClues = getSeenClues(userId);
    let updated = false;
    
    clueIds.forEach(clueId => {
      if (!seenClues.includes(clueId)) {
        seenClues.push(clueId);
        updated = true;
      }
    });
    
    if (updated) {
      localStorage.setItem(storageKey, JSON.stringify(seenClues));
    }
  } catch (error) {
    console.error('Error marking clues as seen in localStorage:', error);
  }
};

/**
 * Check if a clue has been seen
 * @param userId The user ID
 * @param clueId The clue ID to check
 * @returns True if the clue has been seen
 */
export const hasSeenClue = (userId: string, clueId: string): boolean => {
  const seenClues = getSeenClues(userId);
  return seenClues.includes(clueId);
};

/**
 * Get the number of unseen clues
 * @param userId The user ID
 * @param allClueIds Array of all active clue IDs
 * @returns Number of unseen clues
 */
export const getUnseenCluesCount = (userId: string, allClueIds: string[]): number => {
  const seenClues = getSeenClues(userId);
  return allClueIds.filter(clueId => !seenClues.includes(clueId)).length;
};

/**
 * Mark all clues as seen
 * @param userId The user ID
 * @param allClueIds Array of all active clue IDs
 */
export const markAllCluesSeen = (userId: string, allClueIds: string[]): void => {
  try {
    const storageKey = `${SEEN_CLUES_KEY}_${userId}`;
    const seenClues = getSeenClues(userId);
    let updated = false;
    
    allClueIds.forEach(clueId => {
      if (!seenClues.includes(clueId)) {
        seenClues.push(clueId);
        updated = true;
      }
    });
    
    if (updated) {
      localStorage.setItem(storageKey, JSON.stringify(seenClues));
    }
  } catch (error) {
    console.error('Error marking all clues as seen in localStorage:', error);
  }
};
